import * as React from 'react';
import { observer } from 'mobx-react';
import { classes } from '../../utils/Classes';
import { ProductPriceFormatted } from './ProductPriceFormatted';
import { ProductPriceRangeRow } from './ProductPriceRangeRow';
import { IProductPriceRangeConfig, IProductsPrice } from '../../interfaces/IProductPrice';
import {
    ONSALE_TODAY_LABEL,
    PRODUCT_PRICE_PER_UNIT,
    TAX_INCLUDED
} from '../../constants/DataTestIdAttributes';
import { PriceFormatted } from '@estee/elc-formatters';
import { configConsumer, ITranslationsCollection } from '@estee/elc-service';
import { UnitOfMeasureFormattedRegex } from '../../constants/Price';
import { getConfigOverrides, getResponsiveConfig } from '../../utils/ConfigUtils';
import { Sku } from '../../domain/entities/Sku';
import { Breakpoints } from '@estee/elc-base-theme';
import { Desktop, Mobile } from '@estee/elc-layouts';

const productPricePerUnitRowWrapperClassNames = classes('product-price-per-unit-row-wrapper');
const productPricePerUnitClassNames = classes('product-price-per-unit');
const taxIncludedLabelWrapper = classes('product-tax-included-label-wrapper');
const onSaleTodayLabelWrapper = classes('on-sale-today-label-wrapper');

export interface IFormattedPriceTextProps {
    selectedSku: Sku;
    translations?: ITranslationsCollection;
    priceTagElement?: keyof JSX.IntrinsicElements;
}

export const buildFormattedPriceText = (props: IFormattedPriceTextProps) => {
    const { selectedSku, translations } = props;
    const { discountedPricePerUnitMessage = '' } = translations as ITranslationsCollection;
    const { formattedPricePerUnit, unitSizeFormatted } = selectedSku;

    return discountedPricePerUnitMessage
        .replace(UnitOfMeasureFormattedRegex.PRICE, formattedPricePerUnit)
        .replace(UnitOfMeasureFormattedRegex.UNIT_OF_MEASURE, unitSizeFormatted);
};

@configConsumer([
    'hideProductPriceRange',
    'hideProductPricePerUnit',
    'productPriceRange',
    'sitewideOnsaleMessage',
    'productPricePerUnit'
])
@observer
export class ProductPrice extends React.Component<IProductsPrice> {
    private getProductPriceOrPriceRange() {
        const {
            priceRange,
            priceRangeNotDiscounted,
            formattedOriginalPrice,
            formattedPrice,
            isDiscounted,
            componentType,
            selectedSku,
            translations = {},
            priceTagElement,
            config: { hideProductPriceRange, productPriceRange } = {}
        } = this.props;

        let productPriceRangeConfig: IProductPriceRangeConfig = { enabled: !hideProductPriceRange };
        let productPriceRangeDesktopConfig: IProductPriceRangeConfig | undefined;

        if (productPriceRange) {
            const { overrides, ...priceRangeConfig } = productPriceRange;

            productPriceRangeConfig = overrides
                ? { ...priceRangeConfig, ...overrides[componentType] }
                : priceRangeConfig;

            const { responsive } = productPriceRangeConfig;

            productPriceRangeDesktopConfig = responsive
                ? {
                      ...productPriceRangeConfig,
                      ...getResponsiveConfig(Breakpoints.desktop, responsive)
                  }
                : productPriceRangeConfig;
        }
        const responsiveConfig =
            productPriceRangeDesktopConfig && productPriceRangeDesktopConfig.responsive;

        const showProductPriceRange = productPriceRangeConfig
            ? productPriceRangeConfig.enabled
            : !hideProductPriceRange;

        const showProductPriceRangeDesktop = productPriceRangeDesktopConfig
            ? productPriceRangeDesktopConfig.enabled
            : !hideProductPriceRange;

        const isDiscountedPriceRangeEnabled =
            productPriceRangeConfig?.discountedPriceRange || false;

        const responsivePriceRangeConfig =
            responsiveConfig && showProductPriceRange === showProductPriceRangeDesktop
                ? false
                : responsiveConfig;

        const productPriceFormatted = (
            <ProductPriceFormatted
                selectedSku={selectedSku}
                translations={translations}
                priceTagElement={priceTagElement}
            />
        );

        const productPriceRangeRow = priceRange ? (
            <ProductPriceRangeRow
                priceRange={priceRange}
                priceRangeNotDiscounted={priceRangeNotDiscounted}
                isDiscounted={isDiscounted}
                isDiscountedPriceRangeEnabled={isDiscountedPriceRangeEnabled}
                formattedOriginalPrice={formattedOriginalPrice}
                formattedPrice={formattedPrice}
            />
        ) : null;

        return priceRange && (showProductPriceRange || showProductPriceRangeDesktop) ? (
            responsivePriceRangeConfig ? (
                <>
                    <Desktop minWidth={Breakpoints.desktop}>
                        {() => {
                            return (
                                <>
                                    {!showProductPriceRange &&
                                        showProductPriceRangeDesktop &&
                                        productPriceRangeRow}
                                    {showProductPriceRange &&
                                        !showProductPriceRangeDesktop &&
                                        productPriceFormatted}
                                </>
                            );
                        }}
                    </Desktop>
                    <Mobile maxWidth={Breakpoints.desktop - 1}>
                        {() => {
                            return (
                                <>
                                    {!showProductPriceRange &&
                                        showProductPriceRangeDesktop &&
                                        productPriceFormatted}
                                    {showProductPriceRange &&
                                        !showProductPriceRangeDesktop &&
                                        productPriceRangeRow}
                                </>
                            );
                        }}
                    </Mobile>
                </>
            ) : (
                showProductPriceRange && productPriceRangeRow
            )
        ) : (
            productPriceFormatted
        );
    }
    public render() {
        const {
            showIncludeTaxLabel,
            componentType,
            selectedSku,
            translations = {},
            config: { hideProductPricePerUnit, sitewideOnsaleMessage, productPricePerUnit } = {}
        } = this.props;

        const sitewideOnsaleMessageConfig = getConfigOverrides(
            sitewideOnsaleMessage,
            componentType
        );
        const { taxIncluded, onSaleTodayLabel } = translations as ITranslationsCollection;
        const { enabled: enableSitewideOnSaleMessage } = sitewideOnsaleMessageConfig || {};
        const { formattedPricePerUnit: skuFormattedPricePerUnit, sitewideDiscount } = selectedSku;

        const formattedPricePerUnit = sitewideDiscount
            ? buildFormattedPriceText({ selectedSku, translations })
            : skuFormattedPricePerUnit;

        const showSitewideOnSaleMessage = enableSitewideOnSaleMessage && sitewideDiscount;

        const showProductPricePerUnit = productPricePerUnit
            ? getConfigOverrides(productPricePerUnit, componentType).enabled
            : !hideProductPricePerUnit;

        return (
            <>
                {showIncludeTaxLabel && (
                    <div className={taxIncludedLabelWrapper} data-test-id={TAX_INCLUDED}>
                        {taxIncluded}
                    </div>
                )}
                {showSitewideOnSaleMessage && (
                    <div className={onSaleTodayLabelWrapper} data-test-id={ONSALE_TODAY_LABEL}>
                        {onSaleTodayLabel}
                    </div>
                )}
                {this.getProductPriceOrPriceRange()}
                {showProductPricePerUnit && formattedPricePerUnit && (
                    <div
                        className={productPricePerUnitRowWrapperClassNames}
                        data-test-id={PRODUCT_PRICE_PER_UNIT}
                    >
                        <PriceFormatted
                            className={productPricePerUnitClassNames}
                            formattedPrice={formattedPricePerUnit}
                            priceTag="p"
                        />
                    </div>
                )}
            </>
        );
    }
}

export default ProductPrice;
